import React, { createContext, useContext, useState, useMemo } from "react";

const dict = {
  en: {
    nav: { story: "The Story", route: "The Places", different: "Why Different", pricing: "Experiences", book: "Reserve", admin: "Admin" },
    hero: {
      eyebrow: "A slow walk · 2–3 hours · Porto, Portugal",
      title1: "Porto",
      title2: "Beyond the",
      title3: "Postcard",
      script: "a local friend, not a tour guide",
      sub: "This is not a traditional tour. It’s a human experience.",
      paragraph: "No rush. No crowded routes. No endless historical dates. Discover Porto through the eyes of a Venezuelan woman who arrived as a visitor, fell in love with the city and decided to stay.",
      ctaPrimary: "Send Reservation Request",
      ctaSecondary: "See Available Dates",
    },
    guide: {
      eyebrow: "The Storyteller",
      title: "I came to visit. I never really left.",
      quote: "“Porto did not happen to me — it happened with me. I want to walk it with you the same way.”",
      body: "Born in Venezuela, she arrived in Porto with a small suitcase and an open heart. The hills, the tiles, the soft rain at dusk — they convinced her to stay. Today she walks these streets the way a friend would: slowly, attentively, with stories that aren't in any guidebook.",
    },
    details: {
      eyebrow: "The Experience",
      title: "Designed to be felt, not rushed.",
      items: [
        { t: "2 to 3 hours", d: "We move at the rhythm of conversation, not a checklist." },
        { t: "Small groups", d: "An intimate circle, never a tour bus." },
        { t: "Stairs & slopes", d: "Porto is a city of hills. Wear comfortable shoes." },
        { t: "Portuguese flavors", d: "A small tasting is included — chosen, never rushed." },
        { t: "Two languages", d: "Experience available in English or Spanish." },
        { t: "A closing reflection", d: "We end with conversation, not a goodbye." },
      ],
    },
    route: {
      eyebrow: "Beyond the postcard",
      title: "The places everyone sees. The stories most people miss.",
      sub: "Porto is more than its landmarks. It's everything that happens in between.",
      opener: "Where azulejos don't decorate — they remember.",
      chapters: [
        { name: "Sé do Porto", lines: ["Nine centuries of stone.", "Still listening."] },
        { name: "Ribeira do Porto", lines: ["Old voices.", "Narrow alleys.", "The taste of something unhurried."] },
        { name: "Ponte Dom Luís I", lines: ["The iron bridge that turned two riverbanks into one skyline."] },
        { name: "Ribeira de Gaia", lines: ["Cross to the other side.", "Where the light slows down, the river breathes, and Porto begins to look different."] },
        { name: "Largo Dr. Pedro Vitorino Viewpoint", lines: [] },
      ],
      closingLead: "And between them?",
      closing: ["Unexpected corners.", "Quiet details.", "Stories that rarely make it into guidebooks."],
      closingNote: "Because Porto is more than its landmarks. It's everything that happens in between.",
    },
    philosophy: {
      eyebrow: "Our Philosophy",
      title: "Porto, through different eyes.",
      body: "Porto Beyond the Postcard was born from a simple belief: a city is not its monuments, but the feeling of walking through it. We don't rush between landmarks or recite dates. We wander the Ribeira at golden hour, pause where the light pools on the river, and let Porto reveal itself slowly — the way it once revealed itself to a Venezuelan woman who came to visit and never truly left.",
    },
    why: {
      eyebrow: "Why this is different",
      title: "No rushed tourism. Just a slow afternoon in Porto.",
      points: [
        { t: "Human conversations", d: "We talk about food, fado, life — and yes, a little history." },
        { t: "A local emotional lens", d: "Stories of an immigrant who chose this city, told from the inside." },
        { t: "Discovering slowly", d: "We stop. We taste. We listen. We notice." },
        { t: "Memories, not photos", d: "You will leave with something quieter and stronger than souvenirs." },
      ],
    },
    pricing: {
      eyebrow: "Three ways to walk with us",
      title: "Choose the experience that feels like yours.",
      sub: "The same care lives in every walk — what changes is the intimacy, the group size and how personal it feels.",
      mostLoved: "Most popular",
      from: "per person",
      cta: "Choose this",
      includesLabel: "Includes",
      groupLabel: "Group size",
      idealLabel: "Ideal for",
      guests: "guests",
      tiers: [
        {
          id: "essentials", name: "Porto Essentials", price: 14, tag: "The group walk",
          blurb: "For curious travelers who want to discover Porto beyond the postcard in a relaxed group setting.",
          includes: ["Full Porto Fora da Postal experience", "Storytelling and local perspective", "Portuguese flavors tasting", "Reflective ending experience"],
          groupMin: 3, groupMax: 10,
          idealFor: "Solo travelers, friends, backpackers and visitors looking for an authentic introduction to Porto.",
        },
        {
          id: "intimate", name: "Porto Signature Experience", price: 20, tag: "Smaller, closer",
          blurb: "A more intimate version designed for travelers who value deeper conversations, slower travel and smaller groups.",
          includes: ["Smaller group atmosphere", "More personalized interaction", "Enhanced storytelling moments", "Curated local recommendations", "Portuguese flavors tasting", "Reflective ending experience", "Small souvenir included"],
          groupMin: 2, groupMax: 6,
          idealFor: "Couples, solo travelers and those seeking a more personal connection with Porto.",
        },
        {
          id: "signature", name: "Porto Collector's Experience", price: 26, tag: "Boutique & private",
          blurb: "The most curated and personal version of Porto Fora da Postal — for those who want to take home not only memories, but a meaningful keepsake.",
          includes: ["Very small group experience", "Highest level of personal interaction", "Curated storytelling experience", "Portuguese flavors tasting", "Reflective ending experience", "Premium souvenir gift", "Exclusive local recommendations", "Most personalized atmosphere"],
          groupMin: 1, groupMax: 4,
          idealFor: "Travelers looking for a memorable, boutique and highly personal experience.",
        },
      ],
      custom: {
        eyebrow: "Custom Experiences",
        price: "From €30–€46 per person",
        title: "Looking for something different?",
        body: "Private groups, special occasions, women-only experiences, photography-focused walks, personalized routes and tailor-made experiences can be designed upon request.",
        body2: "Contact me directly to discuss your ideas and receive a personalized quotation.",
        cta: "Let's Create Your Porto Experience",
      },
    },
    booking: {
      eyebrow: "Reserve your walk",
      title: "Pick a date. We take care of the rest.",
      sub: "We confirm every booking personally. You will hear from us within a day.",
      pickDate: "Pick a date",
      time: "Time",
      people: "People",
      language: "Language",
      tier: "Experience",
      name: "Your name",
      email: "Email",
      country: "Country",
      whatsapp: "WhatsApp",
      notes: "Anything we should know? (allergies, mobility, mood…)",
      submit: "Send Reservation Request",
      summary: "Summary",
      perPerson: "per person",
      total: "Total",
      success: "Your reservation request was sent.",
      successTitle: "Thank you for your reservation request ❤️",
      successBody: "Your experience is not automatically confirmed yet. I personally review every request to keep the experience intimate and well organized. I will contact you shortly via email or WhatsApp to confirm availability and finalize your booking.",
      preferTalk: "Prefer to talk directly?",
      quoteWhatsapp: "Request a Quote via WhatsApp",
      pending: "Pending personal confirmation",
    },
    contact: {
      eyebrow: "Say hello",
      title: "Walk with us, or just write to us.",
      whatsapp: "Message on WhatsApp",
      instagram: "Follow on Instagram",
    },
    footer: { tagline: "A slow, human walk through Porto.", made: "Made with care in Porto." },
    common: { selectDate: "Select", any: "Any", required: "Required" },
  },
  es: {
    nav: { story: "La historia", route: "Los lugares", different: "Por qué", pricing: "Experiencias", book: "Reservar", admin: "Admin" },
    hero: {
      eyebrow: "Un paseo lento · 2–3 horas · Oporto, Portugal",
      title1: "Oporto",
      title2: "Más Allá de la",
      title3: "Postal",
      script: "una amiga local, no una guía turística",
      sub: "Esto no es un tour tradicional. Es una experiencia humana.",
      paragraph: "Sin prisas. Sin rutas saturadas. Sin fechas históricas interminables. Descubre Oporto a través de los ojos de una venezolana que llegó como visitante, se enamoró de la ciudad y decidió quedarse.",
      ctaPrimary: "Enviar solicitud de reserva",
      ctaSecondary: "Ver fechas disponibles",
    },
    guide: {
      eyebrow: "Quien te acompaña",
      title: "Vine de visita. Nunca me fui del todo.",
      quote: "“Oporto no me sucedió — sucedió conmigo. Quiero caminarla contigo igual.”",
      body: "Venezolana de nacimiento, llegó a Oporto con una maleta pequeña y el corazón abierto. Las cuestas, los azulejos, la llovizna suave al anochecer — la convencieron de quedarse. Hoy camina estas calles como una amiga: despacio, con atención, con historias que no están en ninguna guía.",
    },
    details: {
      eyebrow: "La experiencia",
      title: "Diseñada para sentirse, no para correr.",
      items: [
        { t: "2 a 3 horas", d: "Caminamos al ritmo de la conversación, no de una lista." },
        { t: "Grupos pequeños", d: "Un círculo íntimo, nunca un autobús turístico." },
        { t: "Escaleras y cuestas", d: "Oporto es una ciudad de colinas. Calzado cómodo." },
        { t: "Sabores portugueses", d: "Incluye una pequeña degustación — elegida, sin prisa." },
        { t: "Dos idiomas", d: "Disponible en español o inglés." },
        { t: "Una despedida con calma", d: "Terminamos con conversación, no con un adiós." },
      ],
    },
    route: {
      eyebrow: "Más allá de la postal",
      title: "Los lugares que todos ven. Las historias que casi nadie nota.",
      sub: "Oporto es más que sus monumentos. Es todo lo que sucede entre ellos.",
      opener: "Donde los azulejos no decoran — recuerdan.",
      chapters: [
        { name: "Sé do Porto", lines: ["Nueve siglos de piedra.", "Todavía escuchando."] },
        { name: "Ribeira do Porto", lines: ["Voces antiguas.", "Callejones estrechos.", "El sabor de algo sin prisa."] },
        { name: "Ponte Dom Luís I", lines: ["El puente de hierro que convirtió dos orillas en un mismo horizonte."] },
        { name: "Ribeira de Gaia", lines: ["Cruza al otro lado.", "Donde la luz se vuelve lenta, el río respira y Oporto empieza a verse distinto."] },
        { name: "Largo Dr. Pedro Vitorino Viewpoint", lines: [] },
      ],
      closingLead: "¿Y entre ellos?",
      closing: ["Rincones inesperados.", "Detalles silenciosos.", "Historias que casi nunca llegan a las guías."],
      closingNote: "Porque Oporto es más que sus monumentos. Es todo lo que sucede entre ellos.",
    },
    philosophy: {
      eyebrow: "Nuestra filosofía",
      title: "Oporto, con otros ojos.",
      body: "Oporto Más Allá de la Postal nació de una certeza sencilla: una ciudad no son sus monumentos, sino la emoción de caminarla. No corremos entre lugares ni recitamos fechas. Paseamos por la Ribeira a la hora dorada, nos detenemos donde la luz se posa sobre el río y dejamos que Oporto se revele despacio — como se le reveló a una venezolana que vino de visita y nunca se fue del todo.",
    },
    why: {
      eyebrow: "Por qué es diferente",
      title: "Sin turismo apurado. Solo una tarde lenta en Oporto.",
      points: [
        { t: "Conversaciones humanas", d: "Hablamos de comida, de fado, de la vida — y sí, un poco de historia." },
        { t: "Una mirada local y emocional", d: "Historias de una inmigrante que eligió esta ciudad, contadas desde adentro." },
        { t: "Descubrir despacio", d: "Paramos. Probamos. Escuchamos. Observamos." },
        { t: "Recuerdos, no fotos", d: "Te irás con algo más callado y más fuerte que un souvenir." },
      ],
    },
    pricing: {
      eyebrow: "Tres formas de caminar con nosotros",
      title: "Elige la experiencia que se sienta tuya.",
      sub: "El mismo cuidado vive en cada paseo — lo que cambia es la intimidad, el tamaño del grupo y lo personal que se siente.",
      mostLoved: "Más popular",
      from: "por persona",
      cta: "Elegir esta",
      includesLabel: "Incluye",
      groupLabel: "Tamaño del grupo",
      idealLabel: "Ideal para",
      guests: "personas",
      tiers: [
        {
          id: "essentials", name: "Porto Essentials", price: 14, tag: "El paseo en grupo",
          blurb: "Para viajeros curiosos que quieren descubrir Oporto más allá de la postal en un ambiente de grupo relajado.",
          includes: ["Experiencia completa Porto Fora da Postal", "Narración y mirada local", "Degustación de sabores portugueses", "Cierre reflexivo"],
          groupMin: 3, groupMax: 10,
          idealFor: "Viajeros solos, amigos, mochileros y visitantes que buscan una introducción auténtica a Oporto.",
        },
        {
          id: "intimate", name: "Porto Signature Experience", price: 20, tag: "Más pequeño, más cercano",
          blurb: "Una versión más íntima, pensada para quienes valoran conversaciones más profundas, viajar despacio y grupos pequeños.",
          includes: ["Ambiente de grupo reducido", "Interacción más personalizada", "Momentos de narración ampliados", "Recomendaciones locales curadas", "Degustación de sabores portugueses", "Cierre reflexivo", "Pequeño souvenir incluido"],
          groupMin: 2, groupMax: 6,
          idealFor: "Parejas, viajeros solos y quienes buscan una conexión más personal con Oporto.",
        },
        {
          id: "signature", name: "Porto Collector's Experience", price: 26, tag: "Boutique y privado",
          blurb: "La versión más curada y personal de Porto Fora da Postal — para quienes quieren llevarse a casa no solo recuerdos, sino un detalle con significado.",
          includes: ["Experiencia en grupo muy reducido", "El mayor nivel de interacción personal", "Experiencia de narración curada", "Degustación de sabores portugueses", "Cierre reflexivo", "Regalo souvenir premium", "Recomendaciones locales exclusivas", "Atmósfera más personalizada"],
          groupMin: 1, groupMax: 4,
          idealFor: "Viajeros que buscan una experiencia memorable, boutique y muy personal.",
        },
      ],
      custom: {
        eyebrow: "Experiencias a medida",
        price: "Desde €30–€46 por persona",
        title: "¿Buscas algo diferente?",
        body: "Grupos privados, ocasiones especiales, experiencias solo para mujeres, paseos centrados en fotografía, rutas personalizadas y experiencias a medida se diseñan a petición.",
        body2: "Escríbeme directamente para conversar tus ideas y recibir una cotización personalizada.",
        cta: "Creemos tu experiencia en Oporto",
      },
    },
    booking: {
      eyebrow: "Reserva tu paseo",
      title: "Elige una fecha. Nosotros nos ocupamos del resto.",
      sub: "Confirmamos cada reserva personalmente. Te respondemos en menos de un día.",
      pickDate: "Elige una fecha",
      time: "Hora",
      people: "Personas",
      language: "Idioma",
      tier: "Experiencia",
      name: "Tu nombre",
      email: "Email",
      country: "País",
      whatsapp: "WhatsApp",
      notes: "¿Algo que debamos saber? (alergias, movilidad, ánimo…)",
      submit: "Enviar solicitud de reserva",
      summary: "Resumen",
      perPerson: "por persona",
      total: "Total",
      success: "Tu solicitud de reserva fue enviada.",
      successTitle: "Gracias por tu solicitud de reserva ❤️",
      successBody: "Tu experiencia aún no está confirmada automáticamente. Reviso personalmente cada solicitud para mantener la experiencia íntima y bien organizada. Te contactaré pronto por email o WhatsApp para confirmar la disponibilidad y finalizar tu reserva.",
      preferTalk: "¿Prefieres hablar directamente?",
      quoteWhatsapp: "Solicita un presupuesto por WhatsApp",
      pending: "Pendiente de confirmación personal",
    },
    contact: {
      eyebrow: "Saluda",
      title: "Camina con nosotros, o solo escríbenos.",
      whatsapp: "Mensaje por WhatsApp",
      instagram: "Síguenos en Instagram",
    },
    footer: { tagline: "Un paseo lento y humano por Oporto.", made: "Hecho con cariño en Oporto." },
    common: { selectDate: "Seleccionar", any: "Cualquiera", required: "Requerido" },
  },
};

const LangCtx = createContext({ lang: "en", t: dict.en, setLang: () => {} });

export function LangProvider({ children }) {
  const [lang, setLang] = useState(localStorage.getItem("porto_lang") || "en");
  const value = useMemo(() => ({
    lang,
    t: dict[lang],
    setLang: (l) => { localStorage.setItem("porto_lang", l); setLang(l); },
  }), [lang]);
  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export function useLang() { return useContext(LangCtx); }
